/**
 * EditOrderDialog
 * ---------------
 * Compact modal for editing an order's line items and (optionally) deleting
 * the whole order. Works for both the property-admin "My Orders" tab and the
 * master-admin "Unpaid Expired Orders" panel — the caller passes an
 * `apiBase` (either "/property-admin/me/orders" or "/admin/orders") plus the
 * order to edit.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Check, Trash2, X, Send } from "lucide-react";

const API = getApiUrl();

function currencySymbol(code) {
  return ({ GBP: "£", USD: "$", EUR: "€" }[code] || "£");
}

function ItemEditor({ apiBase, orderId, item, onChanged, onDeleted, currency, disabled }) {
  const [name, setName] = useState(item.name || "");
  const [qty, setQty] = useState(String(item.quantity ?? 1));
  const [price, setPrice] = useState(String(item.price ?? 0));
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const dirty =
    name !== (item.name || "") ||
    Number(qty) !== Number(item.quantity ?? 1) ||
    Number(price) !== Number(item.price ?? 0);

  useEffect(() => {
    setName(item.name || "");
    setQty(String(item.quantity ?? 1));
    setPrice(String(item.price ?? 0));
  }, [item.item_id, item.name, item.quantity, item.price]);

  // Autosave: debounce 800ms after the last keystroke, no manual save click needed.
  useEffect(() => {
    if (!dirty || disabled) return;
    const t = setTimeout(async () => {
      if (Number(qty) < 1) return;
      setSaving(true);
      try {
        const { data } = await axios.put(
          `${API}${apiBase}/${orderId}/items/${item.item_id || item.product_id}`,
          { name, quantity: Number(qty), price: Number(price) },
          { withCredentials: true }
        );
        onChanged(data);
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 1500);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to update item");
      } finally {
        setSaving(false);
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, qty, price]);

  const del = async () => {
    if (!window.confirm(`Remove "${item.name}" from this order?`)) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API}${apiBase}/${orderId}/items/${item.item_id || item.product_id}`,
        { withCredentials: true }
      );
      toast.success("Item removed");
      onDeleted(item.item_id || item.product_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove item");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="grid grid-cols-[1fr,80px,90px,auto] gap-2 items-center border-t border-border py-2"
      data-testid={`edit-order-item-${item.item_id || item.product_id}`}
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="h-8 text-xs rounded-none"
        placeholder="Item name"
        disabled={disabled}
        data-testid={`edit-item-name-${item.item_id || item.product_id}`}
      />
      <Input
        type="number"
        min={1}
        value={qty}
        onChange={(e) => setQty(e.target.value)}
        className="h-8 text-xs rounded-none"
        disabled={disabled}
        data-testid={`edit-item-qty-${item.item_id || item.product_id}`}
      />
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">
          {currencySymbol(currency)}
        </span>
        <Input
          type="number"
          min={0}
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="h-8 text-xs rounded-none pl-5"
          disabled={disabled}
          data-testid={`edit-item-price-${item.item_id || item.product_id}`}
        />
      </div>
      <div className="flex items-center gap-1 w-14 justify-center" data-testid={`edit-item-savestate-${item.item_id || item.product_id}`}>
        {saving ? (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        ) : justSaved ? (
          <Check className="h-3 w-3 text-emerald-600" />
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 rounded-none text-red-600 hover:bg-red-50"
          disabled={deleting || disabled}
          onClick={del}
          data-testid={`edit-item-delete-${item.item_id || item.product_id}`}
          title="Remove item"
        >
          {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
        </Button>
      </div>
    </div>
  );
}

export default function EditOrderDialog({
  open,
  onOpenChange,
  order,
  apiBase = "/property-admin/me/orders",
  onSaved,
  onDeleted,
  notifySupplier, // optional: { supplierId, supplierName, vendorEmail }
}) {
  const [localOrder, setLocalOrder] = useState(order);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [ccVendor, setCcVendor] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => { setLocalOrder(order); setCcVendor(false); }, [order]);

  if (!localOrder) return null;
  const items = localOrder.items || [];

  const onItemChanged = (data) => {
    setLocalOrder((o) => ({
      ...o,
      items: o.items.map((it) =>
        (it.item_id && it.item_id === data?.item?.item_id) ||
        (!it.item_id && it.product_id === data?.item?.product_id)
          ? { ...it, ...(data.item || {}) }
          : it
      ),
    }));
    onSaved && onSaved();
  };

  const onItemDeleted = (removedKey) => {
    setLocalOrder((o) => ({
      ...o,
      items: o.items.filter((it) => (it.item_id || it.product_id) !== removedKey),
    }));
    onSaved && onSaved();
  };

  const deleteWholeOrder = async () => {
    if (!window.confirm("Delete this entire order? This cannot be undone.")) return;
    setDeletingOrder(true);
    try {
      await axios.delete(`${API}${apiBase}/${localOrder.order_id}`, {
        withCredentials: true,
      });
      toast.success("Order deleted");
      onDeleted && onDeleted();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete order");
    } finally {
      setDeletingOrder(false);
    }
  };

  const resendToSupplier = async () => {
    if (!notifySupplier) return;
    setResending(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${localOrder.order_id}/notify-supplier`,
        { supplier_id: notifySupplier.supplierId || null, cc_vendor: ccVendor },
        { withCredentials: true }
      );
      toast.success(
        ccVendor && data.cc?.length
          ? `Email resent to ${notifySupplier.supplierName} (cc: ${data.cc.join(", ")})`
          : `Email resent to ${notifySupplier.supplierName}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend email");
    } finally {
      setResending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto"
        data-testid="edit-order-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            Manage order #{(localOrder.order_id || "").slice(0, 8).toUpperCase()}
          </DialogTitle>
          <DialogDescription>
            {localOrder.customer?.email || ""} · Placed{" "}
            {localOrder.created_at
              ? new Date(localOrder.created_at).toLocaleString()
              : "—"}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-[1fr,80px,90px,auto] gap-2 items-end px-1 text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>Name</span>
          <span>Qty</span>
          <span>Price</span>
          <span></span>
        </div>
        <div className="border border-border">
          {items.length === 0 ? (
            <div className="text-xs text-muted-foreground p-4 text-center">
              No items on this order.
            </div>
          ) : (
            items.map((it) => (
              <ItemEditor
                key={it.product_id || it.item_id}
                apiBase={apiBase}
                orderId={localOrder.order_id}
                item={it}
                currency={localOrder.currency}
                onChanged={onItemChanged}
                onDeleted={onItemDeleted}
                disabled={deletingOrder}
              />
            ))
          )}
        </div>
        <p className="text-[11px] text-muted-foreground -mt-1">Changes save automatically as you type.</p>

        <div className="text-sm text-right pt-2 border-t border-border">
          Total:{" "}
          <span className="font-semibold">
            {currencySymbol(localOrder.currency)}
            {items.reduce((sum, it) => sum + Number(it.price || 0) * Number(it.quantity || 0), 0).toFixed(2)}
          </span>
        </div>

        {notifySupplier && (
          <div className="border border-border bg-muted/30 p-3 space-y-2" data-testid="edit-order-notify-supplier-section">
            <div className="text-xs font-medium">Resend order instruction to {notifySupplier.supplierName}</div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox
                checked={ccVendor}
                onCheckedChange={(v) => setCcVendor(!!v)}
                disabled={!notifySupplier.vendorEmail}
                data-testid="edit-order-cc-vendor-checkbox"
              />
              {notifySupplier.vendorEmail
                ? `CC the vendor (${notifySupplier.vendorEmail})`
                : "CC the vendor (no vendor email on file for this property)"}
            </label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-none"
              disabled={resending}
              onClick={resendToSupplier}
              data-testid="edit-order-resend-supplier-btn"
            >
              {resending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
              Resend email
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="destructive"
            className="rounded-none mr-auto"
            disabled={deletingOrder}
            onClick={deleteWholeOrder}
            data-testid="edit-order-delete-btn"
          >
            {deletingOrder ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-1" /> Delete order
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-none"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
